import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Api from "../../../services/api/Api";
import { public_api } from "../../../services/api/ApiList";
import style from "./AiChatWidget.module.scss";

const SUGGESTIONS = [
  "What services do you offer?",
  "How can I contact your team?",
  "Tell me about your AI products",
];

const MarkdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

export default function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading, isOpen]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen]);

  // Auto-grow textarea
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }, [input]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setInput("");
    setError("");
    setIsLoading(true);

    try {
      const { data } = await Api.post(
        public_api.ai_chat,
        { message: trimmed, conversationId, currentPage: window.location.pathname },
        { timeout: 120000 },
      );
      if (!data?.success_bool || !data?.result_data?.reply) {
        throw new Error(data?.message || "Unable to get a reply.");
      }
      setConversationId(data.result_data.conversationId ?? conversationId);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: data.result_data.reply },
      ]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setConversationId(null);
    setError("");
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  return (
    <div className={style.widget}>
      {isOpen && (
        <section
          className={style.panel}
          role="dialog"
          aria-label="Nathan — LushAITech assistant"
          aria-modal="false"
        >
          <header className={style.header}>
            <div className={style.headerInfo}>
              <div className={style.avatar} aria-hidden="true">
                <ion-icon name="sparkles" style={{ fontSize: "18px", color: "#fff" }} />
              </div>
              <div>
                <p className={style.title}>Nathan</p>
                <p className={style.subtitle}>
                  <span className={style.onlineDot} aria-hidden="true" />
                  Online · LushAITech Assistant
                </p>
              </div>
            </div>
            <div className={style.headerActions}>
              {messages.length > 0 && (
                <button
                  type="button"
                  className={style.headerBtn}
                  onClick={handleClear}
                  aria-label="Clear conversation"
                  title="Clear conversation"
                >
                  <ion-icon name="trash-outline" style={{ fontSize: "18px", color: "#fff" }} />
                </button>
              )}
              <button
                type="button"
                className={style.headerBtn}
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <ion-icon name="close-outline" style={{ fontSize: "18px", color: "#fff" }} />
              </button>
            </div>
          </header>

          <div className={style.messages} aria-live="polite">
            {messages.length === 0 && !isLoading ? (
              <div className={style.emptyState}>
                <div className={style.emptyAvatar} aria-hidden="true">
                  <ion-icon name="sparkles" style={{ fontSize: "28px", color: "#fff" }} />
                </div>
                <p className={style.emptyTitle}>Hi, I&apos;m Nathan!</p>
                <p className={style.emptyDesc}>
                  Your LushAITech assistant. Ask me anything about our services,
                  team, or products — in Mizo or English.
                </p>
                <div className={style.suggestions}>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={style.chip}
                      onClick={() => {
                        setInput(s);
                        inputRef.current?.focus();
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={`${msg.role}-${index}`}
                  className={`${style.messageRow} ${
                    msg.role === "user" ? style.userRow : style.assistantRow
                  }`}
                >
                  {msg.role === "assistant" && (
                    <div className={style.msgAvatar} aria-hidden="true">
                      <ion-icon name="sparkles" style={{ fontSize: "13px", color: "#fff" }} />
                    </div>
                  )}
                  {msg.role === "assistant" ? (
                    <div className={style.assistantBubbleGroup}>
                      <div className={`${style.message} ${style.assistantMessage}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={MarkdownComponents}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                      <button
                        type="button"
                        className={style.copyBtn}
                        onClick={() => handleCopy(msg.text, index)}
                        aria-label="Copy message"
                        title="Copy message"
                      >
                        <ion-icon
                          name={copiedIdx === index ? "checkmark-outline" : "copy-outline"}
                        />
                        {copiedIdx === index ? "Copied" : "Copy"}
                      </button>
                    </div>
                  ) : (
                    <div className={`${style.message} ${style.userMessage}`}>
                      {msg.text}
                    </div>
                  )}
                </div>
              ))
            )}
            {isLoading && (
              <div className={`${style.messageRow} ${style.assistantRow}`}>
                <div className={style.msgAvatar} aria-hidden="true">
                  <ion-icon name="sparkles" style={{ fontSize: "13px", color: "#fff" }} />
                </div>
                <div
                  className={`${style.message} ${style.assistantMessage} ${style.typingIndicator}`}
                >
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {error && <p className={style.error}>{error}</p>}

          <form className={style.composer} onSubmit={handleSend}>
            <textarea
              ref={inputRef}
              className={style.textarea}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message Nathan…"
              rows={1}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
            />
            <button
              type="submit"
              className={style.sendBtn}
              disabled={isLoading || !input.trim()}
              aria-label="Send message"
            >
              <ion-icon name="send" style={{ fontSize: "20px", color: "#fff" }} />
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        className={`${style.trigger} ${isOpen ? style.triggerOpen : ""}`}
        onClick={() => setIsOpen((o) => !o)}
        aria-label={
          isOpen ? "Close chat" : "Chat with Nathan, LushAITech assistant"
        }
        aria-expanded={isOpen}
      >
        <ion-icon name={isOpen ? "close-outline" : "chatbubbles"} style={{ fontSize: "26px", color: "#fff" }} />
        {!isOpen && <span className={style.triggerPulse} aria-hidden="true" />}
      </button>
    </div>
  );
}
